"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FileText, ShieldAlert, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Quickdetect() {
  const highlights = ["Sub-microgram trace narcotics identification", "Non-contact surface & swipe screening", "Direct field deployment with ruggedised casing"];
  return <section id="heroquickdetect" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left 7 Columns */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground bg-secondary flex items-center gap-1.5">
                <ShieldAlert className="size-3.5 text-primary" />
                Safety &amp; Security Product Family
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1]">
                QuickDetect Trace Narcotics Detection
              </h1>
              <p className="font-default text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                Rapid, non-invasive trace screening engineered for frontline defense, customs clearance, and high-throughput law enforcement environments across Ireland and the UK.
              </p>
            </div>

            {/* Feature bullets */}
            <div className="w-full pt-2 pb-2">
              <ul className="grid grid-cols-1 sm:grid-cols-1 gap-3">
                {highlights.map((item, i) => <li key={item} data-index={i} className="flex items-center gap-2.5 text-sm sm:text-base text-foreground/90 font-medium">
                    <CheckCircle2 className="size-4.5 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Dual CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-5 shadow-sm transition-colors">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium px-6 py-5 transition-colors">
                <Link to="/BookADemoPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4 text-muted-foreground" />
                  <span>Book a Demonstration</span>
                </Link>
              </Button>
            </div>

            {/* Operational stats bar */}
            <div className="w-full pt-6 border-t border-border mt-4 grid grid-cols-3 gap-4">
              <div>
                <p className="font-default text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Analysis Speed
                </p>
                <p className="text-lg sm:text-xl font-bold text-foreground mt-0.5 flex items-center gap-1">
                  <Zap className="size-4 text-primary shrink-0" />
                  {"< 10s"}
                </p>
              </div>
              <div>
                <p className="font-default text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Library Base
                </p>
                <p className="text-lg sm:text-xl font-bold text-foreground mt-0.5">
                  {"1,200+ Targets"}
                </p>
              </div>
              <div>
                <p className="font-default text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Compliance
                </p>
                <p className="text-lg sm:text-xl font-bold text-foreground mt-0.5">
                  {"MIL-STD-810G"}
                </p>
              </div>
            </div>
          </div>

          {/* Right 5 Columns */}
          <div className="lg:col-span-5 w-full">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Tactical device photo card */}
              <div className="relative rounded-2xl border border-border bg-card p-3 shadow-lg overflow-hidden">
                <div className="relative overflow-hidden rounded-xl bg-muted aspect-4/3 sm:aspect-5/4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_tactical_device.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_tactical_device.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_tactical_device.png-wvc-sizes" alt="QuickDetect handheld tactical trace narcotics analyzer device" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-foreground/90 text-background backdrop-blur-xs font-mono text-[11px] px-2.5 py-1 shadow-sm border-0">
                      IP67 Ruggedized
                    </Badge>
                  </div>
                </div>

                <div className="p-4 pt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                    <span>Model: QD-500X Tactical</span>
                    <span className="text-primary font-semibold">Active Field Unit</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    Equipped with automated spectral baseline subtraction and single-touch touch-screen verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}